import { Section } from '../components/section';
import { sections } from '../hooks/use-section-analytics';
import { images } from '../constants/images';
import { FormattedString, useMessages } from '../i18n/useMessages';
import { MessageKey } from '../locales/en';
import { Tooltip } from '../components/tooltip';
import { events } from '../pages/kumbh-2025.content';
import { Link } from '@remix-run/react';

const certifications = [
  {
    src: images['ycb'],
    alt: 'Certified by the Yoga Certification Board of Ministry of Ayush',
    title: 'Yoga Certification Board, Ministry of Ayush',
    className: 'max-h-16',
  },
  {
    src: images['mot-medal'],
    alt: 'Certified as an Inbound Tour Operator by the Ministry of Tourism India',
    title: 'Inbound Tour Operator, India',
    className: 'max-h-16',
  },
  {
    src: images['incin-logo'],
    alt: 'Certified under Incredible India',
    title: 'Incredible India',
    className: 'max-h-10',
  },
  {
    src: images['icc'],
    title: 'Indian Chamber of Commerce',
    alt: 'Certified by the Indian Chamber of Commerce',
    className: 'max-h-16',
  },
  {
    src: images['iato'],
    alt: 'Certified by the Indian Association of Tour Operators',
    title: 'Indian Association of Tour Operators, India',
    className: 'max-h-16',
  },
  {
    src: images['mot-govin'],
    alt: 'Certified by the Ministry of Tourism India',
    title: 'Ministry of Tourism, Government of India',
    className: 'max-h-16',
  },
];

const experiences = [
  images['experience-1'],
  images['experience-2'],
  images['experience-3'],
  images['experience-4'],
];

interface SignificantDate {
  dateKey: MessageKey;
  title: MessageKey;
  description: MessageKey;
  date: string;
  endDate: string;
  name: string;
}

const significantDates: SignificantDate[] = events.map(({ name, date, endDate }) => ({
  dateKey: `camp67-one-of-a-kind.calendar.${name}.date`,
  title: `camp67-one-of-a-kind.calendar.${name}.title`,
  description: `camp67-one-of-a-kind.calendar.${name}.description`,
  date,
  endDate,
  name,
}));

export const OneOfAKindSection = () => {
  const getMessage = useMessages();
  const aPremiumDorm = getMessage('camp67-one-of-a-kind.premium.prefix');
  const swtLink = getMessage('camp67-link.sukrit-wellness-tours');
  const fromTeamBehind = getMessage('camp67-one-of-a-kind.team-behind');
  const ksLink = getMessage('camp67-link.kumbh-sukrit-camp');
  const calendarLearnMore = getMessage('camp67-link.learn-more');

  return (
    <Section className="py-16 md:py-20 lg:py-24 -mt-20" id={sections['oneOfAKind']}>
      {/* Location & Dates Banner */}
      <div className="flex justify-center mb-8">
        <a
          href="https://maps.app.goo.gl/Hs1epGZNQ4RohMQJ8"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 px-5 py-2.5 bg-gray-800 text-white rounded-full shadow-md hover:bg-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-sm font-medium">Daraganj, Prayagraj</span>
          <span className="text-gray-400">•</span>
          <span className="text-sm font-medium text-amber-300">
            31 Dec 2025 – End of Feb 2026
          </span>
        </a>
      </div>

      {/* Header Section */}
      <div className="max-w-4xl mx-auto text-center px-6 lg:px-8 mb-12">
        <div className="inline-block mb-3">
          <span className="text-orange-800 font-bold text-xs uppercase tracking-widest bg-orange-100 px-3 py-1.5 rounded-sm">
            Premium Experience
          </span>
        </div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-semibold text-gray-900 mb-4 leading-tight">
          {aPremiumDorm}{' '}
          <a
            className="text-blue-600 hover:text-blue-700 underline decoration-2 underline-offset-4 transition-colors"
            href="https://sukritwellness.com"
            target="_blank"
            rel="noreferrer"
          >
            {swtLink}
          </a>
        </h2>
        <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
          {fromTeamBehind}{' '}
          <a
            href="https://kumbhsukrit.com"
            className="text-blue-600 hover:text-blue-700 underline decoration-2 underline-offset-2 transition-colors"
            target="_blank"
            rel="noreferrer"
          >
            {ksLink}
          </a>
        </p>
      </div>

      {/* Certifications */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-16">
        <ul className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
          {certifications.map(({ title, src, alt, className }, index) => (
            <li
              key={index}
              className="flex flex-col opacity-80 hover:opacity-100 transition-opacity"
            >
              <Tooltip text={title}>
                <img
                  src={src}
                  alt={alt}
                  className={`h-16 md:h-20 object-contain ${className} select-none pointer-events-none filter grayscale hover:grayscale-0 transition-all duration-300`}
                />
              </Tooltip>
            </li>
          ))}
        </ul>
      </div>

      {/* Experience Kumbh Section */}
      <div className="relative py-16 md:py-20">
        {/* Background */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100"
          style={{
            clipPath:
              'polygon(0 4%, 15% 3%, 25% 2%, 35% 1.5%, 45% 1%, 55% 1.5%, 65% 2.5%, 75% 3.5%, 85% 3%, 100% 4.5%, 100% 96%, 85% 97%, 75% 98%, 65% 98.5%, 55% 99%, 45% 98.5%, 35% 97.5%, 25% 96.5%, 15% 97%, 0 95.5%)',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block mb-3">
              <span className="text-orange-800 font-bold text-xs uppercase tracking-widest bg-orange-200 px-3 py-1.5 rounded-sm">
                {getMessage('camp67-experience-kumbh.the-kumbh')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-gray-900 mb-6 leading-tight">
              <FormattedString
                message="camp67-experience-kumbh.engage-unique-experience"
                components={{
                  'unique-highlight': (
                    <span className="text-orange-700 italic">unique</span>
                  ),
                }}
              />
            </h2>
            <p className="text-lg md:text-xl text-gray-800 max-w-3xl mx-auto leading-snug">
              {getMessage('camp67-experience-kumbh.sadhus')}
            </p>
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-8">
              <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 bg-white">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    loading="lazy"
                    src={experiences[1]}
                    alt={getMessage(
                      'camp67-experience-kumbh.naga-sadhu-orange.image-alt',
                    )}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white z-10">
                    <h3 className="text-xl font-bold mb-1">Sacred Traditions</h3>
                    <p className="text-white/90 font-medium max-w-md">
                      Witness the timeless rituals and spiritual devotion
                    </p>
                  </div>
                </div>
              </div>

              <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    loading="lazy"
                    src={experiences[3]}
                    alt={getMessage('camp67-experience-kumbh.canoe.image-alt')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 text-white z-10">
                    <h4 className="text-xl font-bold">Sacred Waters</h4>
                    <p className="text-white/90 font-medium">
                      Journey along the holy Ganges
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-5 space-y-4">
              <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    loading="lazy"
                    src={experiences[0]}
                    alt={getMessage('camp67-experience-kumbh.naga-sadhu.image-alt')}
                    className="w-full h-full object-cover object-[0%_20%] group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs font-bold uppercase tracking-wide">
                      Naga Sadhus
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 text-white z-10">
                    <h4 className="text-lg font-black mb-1">Spiritual Warriors</h4>
                    <p className="text-white/90 font-semibold">
                      Ancient traditions live on
                    </p>
                  </div>
                </div>
              </div>

              <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    loading="lazy"
                    src={experiences[2]}
                    alt={getMessage('camp67-experience-kumbh.naga-sadhu-beard.image-alt')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold uppercase tracking-wide">
                      Devotion
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 text-white z-10">
                    <h4 className="text-lg font-black mb-1">Timeless Wisdom</h4>
                    <p className="text-white/90 font-semibold">Stories etched in time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sacred Dates Calendar Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-16">
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="text-amber-800 font-bold text-xs uppercase tracking-widest bg-amber-50 px-4 py-2 rounded-full">
              Bathing Dates 2026
            </span>
          </div>
          <h3 className="text-xl md:text-2xl lg:text-3xl font-serif font-medium text-gray-900 mb-3">
            Auspicious Dates
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Join us during these spiritually significant dates of the Magh Mela
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {significantDates
            .slice(0, 4)
            .map(({ name, dateKey, title, description }, index) => {
              const formattedTitle = getMessage(title);
              const formattedDescription = getMessage(description);
              const formattedFrom = getMessage(dateKey);

              return (
                <article
                  key={index}
                  className="group bg-white rounded-xl border border-gray-200 hover:border-amber-300 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="p-5 flex flex-col h-full">
                    <div className="mb-4">
                      <span className="inline-block bg-amber-100 text-amber-800 text-xs font-medium uppercase tracking-wider px-2.5 py-1 rounded-md">
                        {formattedFrom}
                      </span>
                    </div>
                    <h4 className="text-lg md:text-xl font-serif font-medium text-gray-900 mb-2 leading-tight">
                      {formattedTitle}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">
                      {formattedDescription}
                    </p>
                    <Link
                      to={`/kumbh-2025/${name}`}
                      prefetch="intent"
                      className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 text-sm font-medium transition-colors duration-200 group/link"
                    >
                      {calendarLearnMore}
                      <svg
                        className="w-3 h-3 transition-transform duration-200 group-hover/link:translate-x-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              );
            })}
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-4xl mx-auto mt-4 lg:mt-6">
          {significantDates
            .slice(4)
            .map(({ name, dateKey, title, description }, index) => {
              const formattedTitle = getMessage(title);
              const formattedDescription = getMessage(description);
              const formattedFrom = getMessage(dateKey);

              return (
                <article
                  key={index + 4}
                  className="group bg-white rounded-xl border border-gray-200 hover:border-amber-300 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="p-5 flex flex-col h-full">
                    <div className="mb-4">
                      <span className="inline-block bg-amber-100 text-amber-800 text-xs font-medium uppercase tracking-wider px-2.5 py-1 rounded-md">
                        {formattedFrom}
                      </span>
                    </div>
                    <h4 className="text-lg md:text-xl font-serif font-medium text-gray-900 mb-2 leading-tight">
                      {formattedTitle}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">
                      {formattedDescription}
                    </p>
                    <Link
                      to={`/kumbh-2025/${name}`}
                      prefetch="intent"
                      className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 text-sm font-medium transition-colors duration-200 group/link"
                    >
                      {calendarLearnMore}
                      <svg
                        className="w-3 h-3 transition-transform duration-200 group-hover/link:translate-x-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              );
            })}
        </div>
      </div>
    </Section>
  );
};
